const PDF_BULLET = /^(?:[•●▪◦]\s+|\?\s+(?=[a-z]))/i;
const PDF_NUMBERED_HEADING = /^\d{1,2}\.\s+[A-Z][A-Z0-9\s&'’/-]{2,}$/;

export const escapeHtml = (value = '') => String(value)
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

const splitPdfMarkers = (value) => String(value || '')
  .replace(/\u00a0/g, ' ')
  .replace(/\r\n?/g, '\n')
  .replace(/\f/g, '\n\n')
  .replace(/\s+(\d{1,2}\.\s+[A-Z][A-Z0-9\s&'’/-]{2,}?)(?=\s+[A-Z][a-z]|\s*$)/g, '\n\n$1\n')
  .replace(/;\s*(?=[•●▪◦?]\s+)/g, ';\n')
  .replace(/\s*([•●▪◦])\s*/g, '\n$1 ')
  .replace(/;\s*(?=[a-z]\.\s+)/g, ';\n');

const joinWrappedLines = (lines) => lines.reduce((result, line) => {
  if (!result) return line;
  return result.endsWith('-') && /^[a-z]/.test(line) ? `${result}${line}` : `${result} ${line}`;
}, '');

/** Convert PDF/document plain text into semantic HTML for the WYSIWYG editor. */
export const pdfTextToHtml = (value = '') => {
  const lines = splitPdfMarkers(value).split('\n').map((line) => line.trim());
  const blocks = [];
  let paragraph = [];
  let list = null;
  const flushParagraph = () => {
    if (!paragraph.length) return;
    const text = joinWrappedLines(paragraph).replace(/\s{2,}/g, ' ').trim();
    if (text) blocks.push(`<p>${escapeHtml(text)}</p>`);
    paragraph = [];
  };
  const flushList = () => {
    if (!list?.items.length) return;
    blocks.push(`<${list.type}>${list.items.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</${list.type}>`);
    list = null;
  };

  lines.forEach((line) => {
    if (!line) {
      flushParagraph();
      flushList();
      return;
    }
    const bullet = line.match(PDF_BULLET);
    if (bullet) {
      flushParagraph();
      if (!list || list.type !== 'ul') { flushList(); list = { type: 'ul', items: [] }; }
      list.items.push(line.replace(PDF_BULLET, '').trim());
      return;
    }
    const lettered = line.match(/^[a-z]\.\s+(.+)/i);
    if (lettered) {
      flushParagraph();
      if (!list || list.type !== 'ol') { flushList(); list = { type: 'ol', items: [] }; }
      list.items.push(lettered[1].trim());
      return;
    }
    if (PDF_NUMBERED_HEADING.test(line)) {
      flushParagraph();
      flushList();
      blocks.push(`<h3>${escapeHtml(line)}</h3>`);
      return;
    }
    // Short contact placeholders such as EMAIL and PHONE are body text;
    // longer all-caps lines are usually document titles from a PDF.
    if (line === line.toUpperCase() && /[A-Z]/.test(line) && (line.length >= 8 || /\s/.test(line)) && !/^\d+\./.test(line)) {
      flushParagraph();
      flushList();
      blocks.push(`<h2>${escapeHtml(line)}</h2>`);
      return;
    }
    flushList();
    paragraph.push(line);
  });
  flushParagraph();
  flushList();
  return blocks.join('');
};

const decodeHtmlEntities = (value) => String(value || '')
  .replace(/&nbsp;/gi, ' ')
  .replace(/&amp;/gi, '&')
  .replace(/&lt;/gi, '<')
  .replace(/&gt;/gi, '>')
  .replace(/&quot;/gi, '"')
  .replace(/&#039;|&#39;/gi, "'")
  .replace(/&#(\d+);/g, (_, code) => String.fromCodePoint(Number(code)))
  .replace(/&#x([\da-f]+);/gi, (_, code) => String.fromCodePoint(parseInt(code, 16)));

/**
 * Older editor versions saved PDF pastes as one preformatted block. Normalize
 * that legacy value before it reaches Tiptap or a public renderer.
 */
export const normalizeLegacyRichText = (value = '') => {
  const source = String(value || '').trim();
  if (!source) return '';
  const preMatch = source.match(/^\s*<pre\b[^>]*>([\s\S]*?)<\/pre>\s*$/i);
  const plain = preMatch ? decodeHtmlEntities(preMatch[1]) : source;
  if (preMatch || !/<[a-z][^>]*>/i.test(source)) return pdfTextToHtml(plain) || `<p>${escapeHtml(plain)}</p>`;
  return source;
};

export const shouldUsePdfTextPaste = (text = '', html = '') => {
  const source = String(text || '').trim();
  if (!source) return false;
  if (!html) return true;
  const hasDocumentMarkers = /(?:^|\s)\d{1,2}\.\s+[A-Z][A-Z\s&'’/-]{3,}/.test(source) || /(?:^|\s)[•●▪◦?]\s+(?=[a-z])/i.test(source) || /(?:^|\n)\s*[a-z]\.\s+/i.test(source);
  const hasSemanticBlocks = /<(?:h[1-6]|p|ul|ol|li|blockquote|table|tr)\b/i.test(html);
  return hasDocumentMarkers && !hasSemanticBlocks;
};
